import { getRequestCredentials, request } from "@prismicio/editor-support/Http"
import {
  Document,
  isRichTextContent,
  isTextContent,
  isUIDContent,
  isWidgetContent,
  traverseDocument,
} from "@prismicio/types-internal/lib/content"
import type { StaticCustomType } from "@prismicio/types-internal/lib/customtypes"
import { z } from "zod"

import { type AuthStrategy } from "../../EditorConfig"

export const metaTitleProperty = "meta_title"
export const metaDescriptionProperty = "meta_description"
export type MetadataType =
  | typeof metaTitleProperty
  | typeof metaDescriptionProperty

interface GenerateSEOMetadataArgs {
  pageTextContent: string
  contentLocale: string
}

const generateSEOMetadataSchema = z.object({
  [metaTitleProperty]: z.string(),
  [metaDescriptionProperty]: z.string(),
})

export async function generateSEOMetadata(
  repository: string,
  baseURL: URL,
  authStrategy: AuthStrategy,
  body: GenerateSEOMetadataArgs,
) {
  const url = new URL("generate-seo-metadata", baseURL)
  url.searchParams.set("repository", repository)

  return request(url, {
    credentials: getRequestCredentials(authStrategy),
    okType: generateSEOMetadataSchema,
    method: "POST",
    body,
  })
}

export const metaTitleConfig = { min: 50, max: 60 } as const
export const metaDescriptionConfig = { min: 120, max: 165 } as const

export function getPageTextContent(
  document: Document,
  customType: StaticCustomType,
) {
  const listOfTextFields: { label: string; value: string }[] = []

  traverseDocument({
    document,
    customType,
  })({
    transformWidget: ({ content, model, apiId }) => {
      if (apiId === metaTitleProperty || apiId === metaDescriptionProperty)
        return undefined

      if (
        (isRichTextContent(content) && model?.type === "StructuredText") ||
        (isTextContent(content) && model?.type === "Text") ||
        (isUIDContent(content) && model?.type === "UID")
      ) {
        const value = extractTextFromField(content)

        if (value) {
          listOfTextFields.push({
            label: model.config?.label || "",
            value: value,
          })
        }
      }
      return undefined
    },
  })

  const pageContent = listOfTextFields
    .map((field) => JSON.stringify(field))
    .join("\n")

  return pageContent
}

export function extractTextFromField(
  contentField: unknown,
): string | undefined {
  if (!isWidgetContent(contentField)) return undefined

  if (contentField.__TYPE__ === "UIDContent") {
    return contentField.value
  }

  if (contentField.__TYPE__ === "StructuredTextContent") {
    const [firstValue] = contentField.value
    if (
      !firstValue ||
      firstValue.type === "image" ||
      firstValue.type === "embed"
    ) {
      return undefined
    }

    return firstValue.content.text
  }

  if (
    contentField.__TYPE__ === "FieldContent" &&
    contentField.type === "Text"
  ) {
    return contentField.value
  }

  return undefined
}

export function isPageContentRelevantForAISeo(pageContent: string): boolean {
  return pageContent.length >= 300
}
